import { createBrowserRouter } from "react-router-dom";

import GlobalLayout from "@/apps/admin/components/GlobalLayout/GlobalLayout";
import { ADMIN_NOT_FOUND_SX } from "@/apps/admin/components/Layout/notFoundSx";
import {
  ADMIN_ROUTE_ERROR,
  lazyPage,
} from "@/apps/admin/components/LazyPage/LazyPage";
import Authenticated from "@/apps/admin/features/authentication/Authenticated/Authenticated";
import PermissionService from "@/apps/admin/features/authentication/Permission/PermissionService";
import NotFound from "@/common/components/NotFound/NotFound";

import { paths } from "./paths";

// The route table lives in ./paths, which pages, TopNav and PageHeader import, so none of them
// imports the router (and every page's loader). Only GlobalLayout still reads it from here, a
// router ↔ GlobalLayout import cycle; the re-export goes when GlobalLayout (P6's) imports ./paths.
export { paths };

export const router = createBrowserRouter([
  {
    path: paths.ROOT.path,
    element: (
      <Authenticated>
        <GlobalLayout />
        <PermissionService />
      </Authenticated>
    ),
    errorElement: ADMIN_ROUTE_ERROR,
    children: [
      {
        index: true,
        ...lazyPage(() => import("@/apps/admin/pages/LandingPage")),
      },
      {
        path: paths.ACCESS_CONTROL.path,
        handle: paths.ACCESS_CONTROL,
        children: [
          {
            index: true,
            ...lazyPage(() => import("@/apps/admin/pages/AccessControl")),
          },
        ],
      },
      {
        path: paths.ORGANIZATIONS.path,
        handle: paths.ORGANIZATIONS,
        children: [
          {
            index: true,
            ...lazyPage(() => import("@/apps/admin/pages/Organizations")),
          },
        ],
      },
      {
        path: paths.GLOBAL_CONTENTS.path,
        handle: paths.GLOBAL_CONTENTS,
        children: [
          {
            path: paths.GLOBAL_CONTENT.path,
            ...lazyPage(
              () => import("@/apps/admin/pages/GlobalContentSummary")
            ),
            handle: paths.GLOBAL_CONTENT,
          },
        ],
      },
      {
        path: paths.STAKEHOLDERS.path,
        handle: paths.STAKEHOLDERS,
        children: [
          {
            index: true,
            ...lazyPage(() => import("@/apps/admin/pages/Stakeholders")),
          },
          {
            path: paths.STAKEHOLDER_DETAILS.path,
            ...lazyPage(() => import("@/apps/admin/pages/StakeholderDetails")),
            handle: paths.STAKEHOLDER_DETAILS,
          },
        ],
      },
      {
        path: paths.SUPERPROMPTS.path,
        handle: paths.SUPERPROMPTS,
        children: [
          {
            index: true,
            ...lazyPage(() => import("@/apps/admin/pages/Superprompts")),
          },
        ],
      },
      {
        path: paths.SCRAPERS.path,
        handle: paths.SCRAPERS,
        children: [
          {
            index: true,
            ...lazyPage(() => import("@/apps/admin/pages/Scrapers")),
          },
          {
            path: paths.SCRAPER_DETAILS.path,
            ...lazyPage(() => import("@/apps/admin/pages/ScraperDetails")),
            handle: paths.SCRAPER_DETAILS,
          },
        ],
      },
      {
        path: paths.INSIGHT_TOOLS.path,
        handle: paths.INSIGHT_TOOLS,
        children: [
          {
            index: true,
            ...lazyPage(
              () => import("@/apps/admin/pages/InsightTools/InsightToolsPage")
            ),
          },
        ],
      },
      {
        path: paths.SPECIAL_PERMISSIONS.path,
        handle: paths.SPECIAL_PERMISSIONS,
        children: [
          {
            index: true,
            ...lazyPage(() => import("@/apps/admin/pages/SpecialPermissions")),
          },
        ],
      },
      {
        path: paths.MANAGE_ASSETS.path,
        handle: paths.MANAGE_ASSETS,
        children: [
          {
            index: true,
            ...lazyPage(
              () => import("@/apps/admin/pages/ManageAssets/ManageAssetsPage")
            ),
          },
        ],
      },
      {
        path: paths.GENIE_KNOWLEDGE.path,
        handle: paths.GENIE_KNOWLEDGE,
        children: [
          {
            index: true,
            ...lazyPage(
              () =>
                import("@/apps/admin/pages/GenieKnowledge/GenieKnowledgePage")
            ),
          },
        ],
      },
      {
        path: paths.GENIE_PLUS_USAGE.path,
        handle: paths.GENIE_PLUS_USAGE,
        children: [
          {
            index: true,
            ...lazyPage(
              () =>
                import("@/apps/admin/pages/GeniePlusUsage/GeniePlusUsagePage")
            ),
          },
        ],
      },
      {
        path: "*",
        handle: { title: "Page not found" },
        element: <NotFound sx={ADMIN_NOT_FOUND_SX} />,
      },
    ],
  },
]);
